/* ==========================================================================
   Ozeret -- dates, clocks and money
   Dates here are civil dates (`YYYY-MM-DD`), never instants. Parsing one with
   `new Date(iso)` would read it as UTC and shift the day in this timezone, so
   everything below builds local dates explicitly.
   ========================================================================== */

import type { Clock, IsoDate } from '../types';
import { DOW, MONTHS } from '../data/catalog';
import { getLang, t } from './i18n';

/* Day and month names in the reader's language. The stored forms ('Mon',
   'Oct') are data and stay English; only what is shown changes. */
const ES_DOW = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const ES_DOW_LETTER = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
const ES_MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];
const es = () => getLang() === 'es';
/** Short weekday by index (0 = Sunday): "Thu" / "Jue". */
export const dowShort = (i: number): string => (es() ? ES_DOW[i] : DOW[i]);
/** One letter, for calendar headings: "T" / "J". */
export const dowLetter = (i: number): string => (es() ? ES_DOW_LETTER[i] : DOW[i][0]);
/** Full month name, capitalised: "October" / "Octubre". */
export const monthName = (i: number): string =>
  (es() ? ES_MONTHS[i][0].toUpperCase() + ES_MONTHS[i].slice(1) : MONTHS[i]);
const monthShort = (i: number): string => (es() ? ES_MONTHS[i].slice(0, 3) : MONTHS[i].slice(0, 3));

/* The clock comes from the server, so every device agrees on what "today"
   means regardless of how its own clock is set. These are live bindings:
   importers see the updated value after setClock() runs. */
export let NOW: Date = new Date();
export let TODAY: Date = new Date(NOW.getFullYear(), NOW.getMonth(), NOW.getDate());

export let TODAY_ISO: IsoDate = isoDate(TODAY);

export function setClock(serverIso: string): void {
  NOW = new Date(serverIso);
  TODAY = new Date(NOW.getFullYear(), NOW.getMonth(), NOW.getDate());
  TODAY_ISO = isoDate(TODAY);
}

export function parseDate(iso: IsoDate): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isoDate(d: Date): IsoDate {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}


/* By the calendar, never by 24-hour steps. The night the clocks go back is
   25 hours long, so midnight + 7 x 24h landed an hour short -- on the day
   before -- and the home's week pager went from "Week of Mon 26 Oct" to
   "Week of Sun 1 Nov", then showed Sunday twice. */
export function addDays(iso: IsoDate, n: number): IsoDate {
  const d = parseDate(iso);
  d.setDate(d.getDate() + n);
  return isoDate(d);
}

/** The Monday of the week containing `iso`. */
export function mondayOf(iso: IsoDate): IsoDate {
  const day = parseDate(iso).getDay();
  return addDays(iso, day === 0 ? -6 : 1 - day);
}

export function minsOf(hhmm: Clock): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function hhmmOf(mins: number): Clock {
  return (
    String(Math.floor(mins / 60)).padStart(2, '0') +
    ':' +
    String(mins % 60).padStart(2, '0')
  );
}

/* "Now", rounded up to the next quarter hour.

   People do not schedule to the minute -- a job starts at half past, or at
   quarter to. Asking somebody to come at 14:07 reads as a machine talking,
   and the first thing anybody would do with it is round it themselves. So we
   round, and we round UP: a start time already in the past is not a start
   time, and 14:07 rounded down to 14:00 is seven minutes ago.

   The one place this needs care is the end of the day. 23:52 rounds to
   midnight, which is not later today -- it is tomorrow, and a request posted
   for 24:00 today would be a request for a time that does not exist. So the
   answer carries its own date. */
export const SLOT_MINUTES = 15;

export function nowSlot(): { date: IsoDate; start: Clock } {
  const mins = NOW.getHours() * 60 + NOW.getMinutes();
  const rounded = Math.ceil(mins / SLOT_MINUTES) * SLOT_MINUTES;
  return rounded >= 24 * 60
    ? { date: addDays(TODAY_ISO, 1), start: '00:00' }
    : { date: TODAY_ISO, start: hhmmOf(rounded) };
}

/** Is this date and time within the next couple of hours? What "Now" means
 *  when it is used as a filter rather than as a start time: somebody looking
 *  for help today, soon, not at some point this evening. */
export const SOON_MINUTES = 150;

export function isSoon(date: IsoDate, start: Clock): boolean {
  if (date !== TODAY_ISO) return false;
  const mins = minsOf(start);
  const now = NOW.getHours() * 60 + NOW.getMinutes();
  /* Something starting a few minutes ago is still "now" to anybody who could
     turn up: a job at two o'clock is worth showing at five past. */
  return mins >= now - 30 && mins <= now + SOON_MINUTES;
}

export function fmtDate(iso: IsoDate, opts: { long?: boolean } = {}): string {
  const d = parseDate(iso);
  const diff = Math.round((d.getTime() - TODAY.getTime()) / 86400000);
  if (!opts.long) {
    if (diff === 0) return t('today');
    if (diff === 1) return t('tomorrow');
    if (diff > 1 && diff < 7) return dowShort(d.getDay()) + ' ' + d.getDate();
    if (diff === -1) return t('tx_yesterday');
  }
  return dowShort(d.getDay()) + ' ' + d.getDate() + ' ' + monthShort(d.getMonth());
}

export function fmtTime(hhmm: Clock): string {
  const [h, m] = hhmm.split(':').map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + (h >= 12 ? 'pm' : 'am');
}

export function endTime(start: Clock, hours: number): string {
  return fmtTime(hhmmOf((minsOf(start) + hours * 60) % 1440));
}

export function ago(at: string): string {
  const mins = Math.round((NOW.getTime() - new Date(at).getTime()) / 60000);
  if (es()) {
    if (mins < 60) return 'hace ' + Math.max(1, mins) + ' min';
    if (mins < 1440) return 'hace ' + Math.round(mins / 60) + ' h';
    return 'hace ' + Math.round(mins / 1440) + ' d';
  }
  if (mins < 60) return Math.max(1, mins) + 'm ago';
  if (mins < 1440) return Math.round(mins / 60) + 'h ago';
  return Math.round(mins / 1440) + 'd ago';
}

export function money(n: number): string {
  return '$' + (Number.isInteger(n) ? n : n.toFixed(2));
}

/** Minutes as "1h 30m". */
export function dur(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  const parts = [h ? h + 'h' : '', m ? m + 'm' : ''].filter(Boolean);
  return parts.join(' ') || '0m';
}

/** Weekdays are stored in the canonical 3-letter form. Spell them out for display. */
const DOW_LONG: Record<string, string> = {
  Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday',
  Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday',
};

const ES_DOW_LONG: Record<string, string> = {
  Sun: 'domingo', Mon: 'lunes', Tue: 'martes', Wed: 'miércoles',
  Thu: 'jueves', Fri: 'viernes', Sat: 'sábado',
};
export function dayLong(d: string): string {
  return (es() ? ES_DOW_LONG[d] : DOW_LONG[d]) || d;
}
